<template>
    <div>
        <v-autocomplete
            browser-autocomplete="new-password"
            :v-model="model"
            :items="predictions"
            :loading="this.isLoading"
            :search-input.sync="search"
            item-text="description"
            :item-avatar="itemAvatar"
            :item-disabled="isPredictionDisabled"
            item-value="place_id"
            return-object
            :menu-props="menuProps"
            :allow-overflow="allowOverflow"
            :append-icon="appendIcon"
            :append-outer-icon="appendOuterIcon"
            :autofocus="autofocus"
            :background-color="backgroundColor"
            :box="box"
            :clear-icon="clearIcon"
            :clearable="clearable"
            :color="color"
            :dark="dark"
            :dense="dense"
            :disabled="disabled"
            :dont-fill-mask-blanks="dontFillMaskBlanks"
            :filter="filter"
            :flat="flat"
            :full-width="fullWidth"
            :height="height"
            :hide-details="hideDetails"
            :hide-no-data="hideNoData"
            :hide-selected="hideSelected"
            :hint="hint"
            :label="label"
            :light="light"
            :multiple="multiple"
            :no-filter="noFilter"
            :open-on-clear="openOnClear"
            :outline="outline"
            :persistent-hint="persistentHint"
            :placeholder="placeholder"
            :prefix="prefix"
            :prepend-icon="prependIcon"
            :prepend-inner-icon="prependInnerIcon"
            :reverse="reverse"
            :rules="rules"
            :single-line="singleLine"
            :solo="solo"
            :solo-inverted="soloInverted"
            :validate-on-blur="validateOnBlur"
            @change="onChange"
            ref="autocomplete"
            class="pt-0"
        >
            <template v-slot:item="{ item }">
                <v-list-tile-content>
                    {{ item.description }}
                </v-list-tile-content>
                <v-chip v-if="!hidePredicted">
                    {{ isPredicted(item) ? 'PREDICTED' : 'MATCH' | translation }}
                </v-chip>
            </template>
        </v-autocomplete>
        <div
            :ref="mapId"
            :v-show="false"
        />
    </div>
</template>

<script>
const BOOLEAN_TYPE_NEGATIVE = {
    type: Boolean,
    default: false,
    required: false,
};
const BOOLEAN_TYPE_POSITIVE = {
    type: Boolean,
    default: true,
    required: false,
};
const STRING_TYPE = {
    type: String,
    required: false,
};

export default {
    name: 'vuetify-google-autocomplete',
    props: {
        addressComponents: {
            type: Object,
            default: () => ({
                street_number: 'short_name',
                route: 'long_name',
                locality: 'long_name',
                administrative_area_level_1: 'short_name',
                country: 'long_name',
                postal_code: 'short_name',
            }),
        },
        allowOverflow: BOOLEAN_TYPE_POSITIVE,
        appendIcon: STRING_TYPE,
        appendOuterIcon: STRING_TYPE,
        autofocus: BOOLEAN_TYPE_NEGATIVE,
        backgroundColor: STRING_TYPE,
        box: BOOLEAN_TYPE_NEGATIVE,
        clearIcon: STRING_TYPE,
        clearable: BOOLEAN_TYPE_NEGATIVE,
        color: STRING_TYPE,
        dark: BOOLEAN_TYPE_NEGATIVE,
        dense: BOOLEAN_TYPE_NEGATIVE,
        disabled: BOOLEAN_TYPE_NEGATIVE,
        dontFillMaskBlanks: BOOLEAN_TYPE_NEGATIVE,
        filter: {
            type: Function,
            required: false,
        },
        flat: BOOLEAN_TYPE_NEGATIVE,
        fullWidth: BOOLEAN_TYPE_NEGATIVE,
        height: {
            type: [String | Number],
            default: null,
            required: false,
        },
        hideDetails: BOOLEAN_TYPE_NEGATIVE,
        hideNoData: BOOLEAN_TYPE_NEGATIVE,
        hideSelected: BOOLEAN_TYPE_POSITIVE,
        hint: STRING_TYPE,
        itemAvatar: {
            type: String,
            default: 'my_location',
        },
        label: STRING_TYPE,
        light: BOOLEAN_TYPE_NEGATIVE,
        menuProps: {
            closeOnClick: true,
            closeOnContentClick: true,
            openOnClick: false,
            maxHeight: 300,
        },
        multiple: BOOLEAN_TYPE_NEGATIVE,
        noFilter: BOOLEAN_TYPE_NEGATIVE,
        openOnClear: BOOLEAN_TYPE_NEGATIVE,
        outline: BOOLEAN_TYPE_NEGATIVE,
        persistentHint: STRING_TYPE,
        placeholder: STRING_TYPE,
        prefix: STRING_TYPE,
        prependIcon: STRING_TYPE,
        prependInnerIcon: STRING_TYPE,
        reverse: BOOLEAN_TYPE_NEGATIVE,
        rules: {
            type: Array,
            default: () => [],
            required: false,
        },
        singleLine: BOOLEAN_TYPE_NEGATIVE,
        solo: BOOLEAN_TYPE_NEGATIVE,
        soloInverted: BOOLEAN_TYPE_NEGATIVE,
        hidePredicted: BOOLEAN_TYPE_POSITIVE,
        disablePredicted: BOOLEAN_TYPE_NEGATIVE,
        validateOnBlur: BOOLEAN_TYPE_NEGATIVE,
        city: null,
        postCode: null,
        street: null,
        houseNumber: null,
        country: null,
    },
    data: () => ({
        autocompleteService: null,
        placesService: null,
        autocompleteText: '',
        vgaMapState: null,
        predictions: [],
        model: null,
        isLoading: false,
        search: null,
    }),
    methods: {
        setupGoogle() {
            this.autocompleteService = new google.maps.places.AutocompleteService();
            const map = new google.maps.Map(this.$refs[this.mapId]);
            this.placesService = new google.maps.places.PlacesService(map);
        },
        isPredictionDisabled(prediction) {
            return this.isPredicted(prediction) && this.disablePredicted;
        },
        isPredicted(prediction) {
            return prediction.place_id.length > 27;
        },
        onChange(prediction) {
            if (prediction && prediction.place_id) {
                this.placesService.getDetails(
                    {
                        placeId: prediction.place_id,
                    },
                    place => {
                        const returnData = {};
                        if (place) {
                            if (place.address_components !== undefined) {
                                for (let i = 0; i < place.address_components.length; i += 1) {
                                    const addressType = place.address_components[i].types[0];

                                    if (this.addressComponents[addressType]) {
                                        const val = place.address_components[i][this.addressComponents[addressType]];
                                        returnData[addressType] = val;
                                    }
                                }

                                returnData.latitude = place.geometry.location.lat();
                                returnData.longitude = place.geometry.location.lng();

                                returnData.name = place.name;
                                returnData.photos = place.photos;
                                returnData.place_id = place.place_id;

                                this.onAutocompleteChanged(returnData, place);
                                this.$emit('placechanged', returnData, place, this.id);
                                this.$refs.autocomplete.clearableCallback();
                            }
                        }
                    },
                );
            }
        },
        onAutocompleteChanged(returnData, place) {
            place.address_components.forEach(addressComponent => {
                const map = {
                    city: 'locality',
                    postCode: 'postal_code',
                    street: 'route',
                    houseNumber: 'street_number',
                    country: 'country',
                };

                Object.keys(map).forEach(key => {
                    let type = map[key];

                    if (addressComponent.types.includes(type)) {
                        let value = addressComponent.long_name;
                        if ('country' == key) {
                            value = addressComponent.short_name;
                        }
                        this.$emit(`update:${key}`, value);
                    }
                });
            });
        },
    },
    computed: {
        mapId() {
            return `${this.$attrs.id}_map`;
        },
    },
    created() {
        this.autocompleteText = this.value ? this.value : '';
    },
    mounted() {
        this.vgaMapState = window.vgaMapState;
        if (Object.prototype.hasOwnProperty.call(window, 'google')) {
            this.setupGoogle();
        }
    },
    destroyed() {
        window.vgaMapState.initMap = false;
    },
    watch: {
        'vgaMapState.initMap'(value) {
            if (value) {
                this.setupGoogle();
            }
        },
        search(input) {
            this.model = input;
            if (this.model && this.model.length) {
                this.isLoading = true;
                this.autocompleteService.getPlacePredictions(
                    {
                        input: input,
                        types: ['address'],
                        componentRestrictions: {
                            country: Object.keys(this.$country.getCountries()),
                        },
                    },
                    predictions => {
                        if (predictions) {
                            let array = [];
                            predictions.forEach(prediction => {
                                if (!this.isPredicted(prediction) || !this.hidePredicted) {
                                    array.push(prediction);
                                }
                            });
                            this.predictions = array;
                        } else {
                            this.predictions = [];
                        }
                        this.isLoading = false;
                    },
                );
            } else {
                this.predictions = [];
            }
        },
    },
};
</script>
